import { useState, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { ref, set, onValue, off } from 'firebase/database';
import { db, rtdb } from '../lib/firebase';
import { UserChallenge } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export const useChallenges = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  const submitChallenge = useCallback(async (
    roomCode: string,
    targetPlayerId: string,
    templateId: string,
    templateText: string,
    challengeText: string
  ): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('📝 Submitting challenge to Firebase:', {
        roomCode,
        targetPlayerId,
        templateId,
        challengeText: challengeText.slice(0, 50) + '...',
        authorId: user.uid
      });

      const challengesRef = collection(db, 'rooms', roomCode, 'userChallenges');
      
      const challengeData: Omit<UserChallenge, 'id'> = {
        authorId: user.uid,
        targetPlayerId,
        templateId,
        templateText,
        challengeText: challengeText.trim(),
        revealed: false,
        createdAt: Timestamp.now()
      };

      await addDoc(challengesRef, challengeData);
      
      console.log('✅ Challenge submitted successfully');
    } catch (error: any) {
      console.error('❌ Error submitting challenge:', error);
      showError(error.message || 'Error al enviar el desafío');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError]);

  const getChallengesByPlayer = useCallback(async (
    roomCode: string,
    playerId: string
  ): Promise<UserChallenge[]> => {
    try {
      console.log('📋 Getting challenges for player:', playerId);

      const challengesRef = collection(db, 'rooms', roomCode, 'userChallenges');
      const q = query(
        challengesRef,
        where('authorId', '==', playerId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const challenges = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserChallenge[];

      console.log('✅ Retrieved challenges:', challenges.length);
      return challenges;
    } catch (error: any) {
      console.error('❌ Error getting challenges:', error);
      showError(error.message || 'Error al obtener desafíos');
      return [];
    }
  }, [showError]);

  const getAllChallenges = useCallback(async (roomCode: string): Promise<UserChallenge[]> => {
    try {
      console.log('📋 Getting all challenges for room:', roomCode);

      const challengesRef = collection(db, 'rooms', roomCode, 'userChallenges');
      const q = query(challengesRef, orderBy('createdAt', 'asc'));

      const querySnapshot = await getDocs(q);
      const challenges = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserChallenge[];

      console.log('✅ Retrieved all challenges:', challenges.length);
      return challenges;
    } catch (error: any) {
      console.error('❌ Error getting all challenges:', error);
      return [];
    }
  }, []); // Remove showError dependency to make it stable

  const markChallengeAsRevealed = useCallback(async (
    roomCode: string,
    challengeId: string
  ): Promise<void> => {
    try {
      console.log('🔓 Marking challenge as revealed:', challengeId);

      const challengeRef = doc(db, 'rooms', roomCode, 'userChallenges', challengeId);
      await updateDoc(challengeRef, {
        revealed: true
      });

      console.log('✅ Challenge marked as revealed');
    } catch (error: any) {
      console.error('❌ Error marking challenge as revealed:', error);
      showError(error.message || 'Error al marcar desafío como revelado');
      throw error;
    }
  }, [showError]);

  const checkAllPlayersReady = useCallback(async (
    roomCode: string,
    totalPlayers: number
  ): Promise<boolean> => {
    try {
      const allChallenges = await getAllChallenges(roomCode);
      
      // Each player should write exactly 3 challenges
      // So if there are N players, there should be N * 3 total challenges
      const expectedChallenges = totalPlayers * 3;
      
      console.log('🔍 Checking if all players ready:', {
        totalChallenges: allChallenges.length,
        expectedChallenges,
        totalPlayers,
        challengesPerPlayer: 3
      });
      
      return allChallenges.length >= expectedChallenges;
    } catch (error: any) {
      console.error('❌ Error checking if all players ready:', error);
      return false;
    }
  }, [getAllChallenges]);

  const transitionToRevealPhase = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🔄 Transitioning room to reveal phase:', roomCode);

      // Update Firestore room status
      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'CHALLENGE_REVEAL',
        currentRoundIndex: 0
      });

      // Initialize reveal state in Realtime Database (basic setup)
      const revealRef = ref(rtdb, `revealState/${roomCode}`);
      
      // Only set basic state, let ChallengeReveal component set the detailed state
      await set(revealRef, {
        currentChallengeIndex: 0,
        isRevealed: false,
        totalChallenges: 0,
        isFinished: false,
        lastUpdated: Date.now(),
        initialized: false, // Flag to indicate this is just the initial setup
        challengeOrder: [] // Will store the shuffled order of challenge IDs
      });

      console.log('✅ Room transitioned to reveal phase with real-time sync');
      showSuccess('¡Comenzando la revelación de desafíos!');
    } catch (error: any) {
      console.error('❌ Error transitioning to reveal phase:', error);
      showError(error.message || 'Error al iniciar la revelación');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess]);

  const updateRevealState = useCallback(async (
    roomCode: string,
    updates: {
      currentChallengeIndex?: number;
      isRevealed?: boolean;
      totalChallenges?: number;
      isFinished?: boolean;
      challengeOrder?: string[];
    }
  ): Promise<void> => {
    try {
      const revealRef = ref(rtdb, `revealState/${roomCode}`);
      const timestamp = Date.now();
      
      // Create complete state object with required fields
      const newState = {
        currentChallengeIndex: updates.currentChallengeIndex ?? 0,
        isRevealed: updates.isRevealed ?? false,
        totalChallenges: updates.totalChallenges ?? 0,
        isFinished: updates.isFinished ?? false,
        challengeOrder: updates.challengeOrder ? updates.challengeOrder.reduce((obj, id, index) => ({ ...obj, [index]: id }), {}) : {},
        initialized: true, // Mark as properly initialized
        lastUpdated: timestamp
      };
      
      console.log('🔄 HOST: Updating reveal state:', newState);
      await set(revealRef, newState);
    } catch (error: any) {
      console.error('❌ Error updating reveal state:', error);
      throw error;
    }
  }, []);

  const subscribeToRevealState = useCallback((
    roomCode: string,
    callback: (state: {
      currentChallengeIndex: number;
      isRevealed: boolean;
      totalChallenges: number;
      isFinished: boolean;
      challengeOrder?: string[];
      initialized?: boolean;
      lastUpdated?: number;
    }) => void
  ) => {
    console.log('🔗 Creating reveal state subscription for room:', roomCode, 'at path: revealState/' + roomCode);
    const revealRef = ref(rtdb, `revealState/${roomCode}`);
    
    const unsubscribe = onValue(revealRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        console.log('🔥 Firebase RTDB data received:', {
          initialized: data.initialized,
          challengeOrderLength: data.challengeOrder?.length || 0,
          challengeOrderIds: data.challengeOrder || [],
          currentIndex: data.currentChallengeIndex,
          totalChallenges: data.totalChallenges,
          allDataKeys: Object.keys(data),
          fullData: data
        });
        callback(data);
      }
    });

    return () => {
      console.log('🔗 Unsubscribing from reveal state for:', roomCode);
      off(revealRef, 'value', unsubscribe);
    };
  }, []); // Stable function

  const finishChallengeGame = useCallback(async (roomCode: string, deleteRoom?: (roomCode: string) => Promise<void>): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🏁 Finishing challenge game:', roomCode);

      // Update Firestore room status to ENDED first (so other players can navigate to summary)
      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'ENDED'
      });

      // Mark reveal as finished in RTDB
      await updateRevealState(roomCode, {
        isFinished: true
      });

      console.log('✅ Challenge game status updated to ENDED');

      // Give other players time to navigate to summary (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Delete the room completely to disconnect all players
      if (deleteRoom) {
        console.log('🗑️ Deleting room to disconnect all players...');
        await deleteRoom(roomCode);
        console.log('✅ Room deleted - all players disconnected');
      }

      showSuccess('¡Juego terminado!');
    } catch (error: any) {
      console.error('❌ Error finishing challenge game:', error);
      showError(error.message || 'Error al finalizar el juego');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess, updateRevealState]);

  return {
    loading,
    submitChallenge,
    getChallengesByPlayer,
    getAllChallenges,
    markChallengeAsRevealed,
    checkAllPlayersReady,
    transitionToRevealPhase,
    updateRevealState,
    subscribeToRevealState,
    finishChallengeGame
  };
};